import { type ComponentType } from "react"
import { Container, Dialog, Stack, Typography } from "@octopus-energy/coral/atoms"
import {
    ConstantineCalculating,
    ConstantineCoin,
    ConstantineCycling,
    type ConstantineProps,
} from "@octopus-energy/coral/media"
import { noop } from "@oefr/utils/react"
import { useTranslations } from "next-intl"
import { type IntlKeys } from "@/features/translations"
import { useEligibilityLoadingSteps } from "../hooks/useEligibilityLoadingSteps"
import { EligibilityStepReveal } from "./EligibilityStepReveal"

type EligibilityLoadingModalDescriptionIntlKey = IntlKeys<"EligibilityLoadingModal.description">

type EligibilityLoadingStep = {
    descriptionKey: EligibilityLoadingModalDescriptionIntlKey
    Media: ComponentType<ConstantineProps>
    size: number
}

const DESCRIPTION_REVEAL_DELAY_IN_SEC = 0.15

const MEDIA_HEIGHT = 130

// `size` sets width; scaled per animation's own aspect ratio so all three render at the same height
const STEPS = [
    {
        descriptionKey: "informationSearch",
        Media: ConstantineCycling,
        size: Math.round(MEDIA_HEIGHT * 1.77),
    },
    { descriptionKey: "consumptionRetrieval", Media: ConstantineCalculating, size: MEDIA_HEIGHT },
    { descriptionKey: "quotePreparation", Media: ConstantineCoin, size: MEDIA_HEIGHT },
] as const satisfies EligibilityLoadingStep[]

type EligibilityLoadingModalProps = {
    isLoading?: boolean
}

export function EligibilityLoadingModal({ isLoading = false }: EligibilityLoadingModalProps) {
    const t = useTranslations("EligibilityLoadingModal")
    const stepIndex = useEligibilityLoadingSteps(STEPS.length)
    const { descriptionKey, Media, size } = STEPS[stepIndex]

    return (
        <Dialog
            open={isLoading}
            onClose={noop}
            ariaLabelledBy={t("label")}
            disableEscapeKeyDown
        >
            <Container height={300}>
                <Stack
                    fullHeight
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography
                        textStyle="h1"
                        textAlign="center"
                        color="tertiary500"
                    >
                        {t("title")}
                    </Typography>

                    <EligibilityStepReveal stepKey={stepIndex}>
                        <Media size={size} />
                    </EligibilityStepReveal>

                    <EligibilityStepReveal
                        stepKey={stepIndex}
                        delay={DESCRIPTION_REVEAL_DELAY_IN_SEC}
                    >
                        <Typography textAlign="center">
                            {t(`description.${descriptionKey}`)}
                        </Typography>
                    </EligibilityStepReveal>
                </Stack>
            </Container>
        </Dialog>
    )
}

EligibilityLoadingModal.messages = ["EligibilityLoadingModal"] as const
